import type { GlobalSearch } from '#shared/utils/solr-search'
import type { RouteLocationRaw } from 'vue-router'
import type { EnrichedSearchResult, SearchResult } from './search'
import { resolveSolrDocumentTitle } from '#shared/utils/solr'
import { normalizeSolrTitleForComparison } from '#shared/utils/solr-title'

const AUTHOR_SEPARATOR_REGEX = /,\s+| og /

/**
 * Maps lowercase label → nodeTypes array
 * Used by facet aggregation and filter building
 */
export const labelToNodeTypes: Record<string, readonly string[]> = Object.fromEntries(
  Object.entries(CONTENT_TYPE_OPTIONS).map(([label, config]) => [
    label.toLowerCase(),
    config.nodeTypes,
  ]),
)

/**
 * Get display label for a nodeType
 */
export function nodeTypeToLabels(nodeType?: string | null): string {
  if (!nodeType) {
    return 'Ukjent'
  }

  if (nodeType in NODE_TYPE_DISPLAY_LABELS) {
    return NODE_TYPE_DISPLAY_LABELS[nodeType as keyof typeof NODE_TYPE_DISPLAY_LABELS]
  }

  return nodeType.charAt(0).toUpperCase() + nodeType.slice(1)
}

export function resolveTitle(it: GlobalSearch): ReturnType<typeof resolveSolrDocumentTitle> {
  return resolveSolrDocumentTitle(it)
}

function isSearchResultParentTitleComposed(result: SearchResult): boolean {
  const title = normalizeSolrTitleForComparison(result.title)
  const parentTitle = normalizeSolrTitleForComparison(result.subTitle)
  return Boolean(parentTitle && title.startsWith(`${parentTitle} / `))
}

export function shouldShowSeparateTitleContext(result: SearchResult): boolean {
  return result.journalIssueTitle !== undefined && !isSearchResultParentTitleComposed(result)
}

export function getSearchResultTitleContext(
  result: SearchResult,
  additionalContext: Array<string | null | undefined> = [],
): string[] {
  const seen = new Set([normalizeSolrTitleForComparison(result.title)].filter(Boolean))
  const context = [
    isSearchResultParentTitleComposed(result) ? null : result.subTitle,
    result.journalIssueTitle,
    ...additionalContext,
  ]

  return context.filter((part): part is string => {
    const normalized = normalizeSolrTitleForComparison(part)
    if (!normalized || seen.has(normalized)) {
      return false
    }
    seen.add(normalized)
    return true
  })
}

export function getSearchResultAccessibleLabel(result: SearchResult): string {
  const title = result.title ?? ''
  return shouldShowSeparateTitleContext(result)
    ? [title, ...getSearchResultTitleContext(result)].filter(Boolean).join(' – ')
    : title
}

// TODO: We might still want to include the search term based on the criteria below, will be conluded after the workshop
export function shouldIncludeSearchTerm(doc: SearchResult, highlighting: Record<string, Record<string, string[] | null | undefined>>): boolean {
  // Author, journal pages don't support in-document search
  if (doc.nodeType === 'author' || doc.nodeType === 'journal') {
    return false
  }

  const docHighlight = highlighting[doc.id]

  if (!docHighlight) {
    return false
  }

  const hasContentMatch = !!(docHighlight.bodytext?.length || docHighlight.abstract?.length)

  return hasContentMatch
}

export function resolveSearchResult(item: EnrichedSearchResult, hasLicense: boolean): RouteLocationRaw | undefined {
  if (hasLicense) {
    return item.url
  }

  // TODO: Verify if we still need to redirect to book about page for unathorized users
  if (item.nodeType === 'book') {
    return { name: 'book-about-by-id', params: { bokId: item.id } }
  }

  if ((item.nodeType === 'journal_article' || item.nodeType === 'journal_editorial') && item.publisherId) {
    return { name: 'journal-publication', params: { publisherId: [item.publisherId] } }
  }

  return item.url
}

export function truncateAuthors(authors: string | undefined, maxLength: number): string {
  if (!authors) {
    return ''
  }

  const names = authors.split(AUTHOR_SEPARATOR_REGEX)
  if (names.length <= maxLength) {
    return authors
  }

  return `${names.slice(0, maxLength).join(', ')} m.fl.`
}
